import Icon from "../Icon";
import { useRef, useState } from "react";
import { DetailsSummaryProps } from "./types";
import { styles } from "./styles";
import Truncated from "../Truncated";

/**
 * `DetailsSummary` is a wrapper around the native HTML `<details>` and `<summary>` elements,
 * providing a consistent style and additional features such as an optional toggle icon and actions.
 *
 * Supports two modes:
 * - **Controlled**: When `onToggle` is provided, `isOpen` is treated as the controlled open state.
 *   The parent must update `isOpen` in response to `onToggle` to reflect changes.
 * - **Uncontrolled**: When `onToggle` is not provided, `isOpen` acts as the initial open state
 *   and the native `<details>` toggle behavior takes over.
 */
function DetailsSummary({
  title,
  className,
  children,
  isOpen: propsIsOpen,
  onToggle,
  variant = "default",
  actions
}: DetailsSummaryProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [internalIsOpen, setInternalIsOpen] = useState(propsIsOpen);

  const isControlled = onToggle !== undefined;
  const isOpen = isControlled ? propsIsOpen : internalIsOpen;

  return (
    <details
      ref={detailsRef}
      className={`kit-DetailsSummary${className ? ` ${className}` : ""}`}
      css={[styles.base, styles.variant[variant]]}
      open={isOpen}
      onToggle={e => {
        if (detailsRef.current) {
          const nextOpen = detailsRef.current.open;
          if (!isControlled) {
            setInternalIsOpen(nextOpen);
          }
          onToggle?.(nextOpen, e);
        }
      }}
    >
      <summary css={[styles.summary]}>
        {typeof title === "string" ? <Truncated text={title} /> : title}

        {actions && (
          <span className="kit-details-summary-actions" css={styles.actions}>
            {actions}
          </span>
        )}

        <span
          className="kit-details-summary-toggle-icon"
          css={styles.toggleIcon}
        >
          <Icon name="Chevron" direction="down" size="fill" />
        </span>
      </summary>
      {children}
    </details>
  );
}

export default DetailsSummary;
